import { isAxiosError } from "axios";
import type {
  Form,
  FormStep1EnergyConsumption,
  FormStep1EnergyConsumptionResponse,
  FormStep2OperatorAndTariff,
  FormStep2OperatorAndTariffResponse,
  FormStep3EnergyBilling,
  FormStep3EnergyBillingResponse,
  FormStep4ConnectionAndInstallation,
  FormStep4ConnectionAndInstallationResponse,
  FormStep5Customer,
  FormStep5CustomerResponse,
} from "@/types/Form";
import { toEnergyConsumptionFormData } from "@/utils/data";
import Api from "./Api";

export class FormApi {
  static async getOne(id: string) {
    const { data } = await Api.get<Form>(`/form-submission/${id}/`);
    return data;
  }

  static async create() {
    const { data } = await Api.post<Form>("/form-submission/");
    return data;
  }

  static async update(id: string, clientId: string) {
    const { data } = await Api.patch<Form>(`/form-submission/${id}/`, { customer: clientId });
    return data;
  }

  static async getStep1EnergyConsumption(formId: string) {
    const { data } = await Api.get<FormStep1EnergyConsumptionResponse>(
      `/form-submission/${formId}/energy-consumption/`,
    );
    return data;
  }

  static async postStep1EnergyConsumption(formId: string, payload: FormStep1EnergyConsumption) {
    const { data } = await Api.post<FormStep1EnergyConsumptionResponse>(
      `/form-submission/${formId}/energy-consumption/`,
      toEnergyConsumptionFormData(payload),
    );
    return data;
  }

  static async patchStep1EnergyConsumption(formId: string, payload: FormStep1EnergyConsumption) {
    const { data } = await Api.patch<FormStep1EnergyConsumptionResponse>(
      `/form-submission/${formId}/energy-consumption/`,
      toEnergyConsumptionFormData(payload),
    );
    return data;
  }

  static async upsertStep1EnergyConsumption(formId: string, payload: FormStep1EnergyConsumption) {
    try {
      return await FormApi.patchStep1EnergyConsumption(formId, payload);
    } catch (err) {
      if (isAxiosError(err)) {
        const status = err.response?.status;
        if (status === 404 || status === 405) {
          return await FormApi.postStep1EnergyConsumption(formId, payload);
        }
      }
      throw err;
    }
  }

  static async getStep2OperatorAndTariff(formId: string) {
    const { data } = await Api.get<FormStep2OperatorAndTariffResponse>(
      `/form-submission/${formId}/operator-and-tariff/`,
    );
    return data;
  }

  static async postStep2OperatorAndTariff(formId: string, payload: FormStep2OperatorAndTariff) {
    const { data } = await Api.post<FormStep2OperatorAndTariffResponse>(
      `/form-submission/${formId}/operator-and-tariff/`,
      payload,
    );
    return data;
  }

  static async patchStep2OperatorAndTariff(
    formId: string,
    payload: Partial<FormStep2OperatorAndTariff>,
  ) {
    const { data } = await Api.patch<FormStep2OperatorAndTariffResponse>(
      `/form-submission/${formId}/operator-and-tariff/`,
      payload,
    );
    return data;
  }

  static async upsertStep2OperatorAndTariff(formId: string, payload: FormStep2OperatorAndTariff) {
    try {
      return await FormApi.patchStep2OperatorAndTariff(formId, payload);
    } catch (err) {
      if (isAxiosError(err)) {
        const status = err.response?.status;
        if (status === 404 || status === 405) {
          return await FormApi.postStep2OperatorAndTariff(formId, payload);
        }
      }
      throw err;
    }
  }

  static async getStep3EnergyBilling(formId: string) {
    const { data } = await Api.get<FormStep3EnergyBillingResponse>(
      `/form-submission/${formId}/energy-billing/`,
    );
    return data;
  }

  static async postStep3EnergyBilling(formId: string, payload: FormStep3EnergyBilling) {
    const { data } = await Api.post<FormStep3EnergyBillingResponse>(
      `/form-submission/${formId}/energy-billing/`,
      payload,
    );
    return data;
  }

  static async patchStep3EnergyBilling(formId: string, payload: Partial<FormStep3EnergyBilling>) {
    const { data } = await Api.patch<FormStep3EnergyBillingResponse>(
      `/form-submission/${formId}/energy-billing/`,
      payload,
    );
    return data;
  }

  static async upsertStep3EnergyBilling(formId: string, payload: FormStep3EnergyBilling) {
    try {
      return await FormApi.patchStep3EnergyBilling(formId, payload);
    } catch (err) {
      if (isAxiosError(err)) {
        const status = err.response?.status;
        if (status === 404 || status === 405) {
          return await FormApi.postStep3EnergyBilling(formId, payload);
        }
      }
      throw err;
    }
  }

  static async getStep4ConnectionAndInstallation(formId: string) {
    const { data } = await Api.get<FormStep4ConnectionAndInstallationResponse>(
      `/form-submission/${formId}/connection-and-installation/`,
    );
    return data;
  }

  static async postStep4ConnectionAndInstallation(
    formId: string,
    payload: FormStep4ConnectionAndInstallation,
  ) {
    const { data } = await Api.post<FormStep4ConnectionAndInstallationResponse>(
      `/form-submission/${formId}/connection-and-installation/`,
      payload,
    );
    return data;
  }

  static async patchStep4ConnectionAndInstallation(
    formId: string,
    payload: FormStep4ConnectionAndInstallation,
  ) {
    const { data } = await Api.patch<FormStep4ConnectionAndInstallationResponse>(
      `/form-submission/${formId}/connection-and-installation/`,
      payload,
    );
    return data;
  }

  static async upsertStep4ConnectionAndInstallation(
    formId: string,
    payload: FormStep4ConnectionAndInstallation,
  ) {
    try {
      return await FormApi.patchStep4ConnectionAndInstallation(formId, payload);
    } catch (err) {
      if (isAxiosError(err)) {
        const status = err.response?.status;
        if (status === 404 || status === 405) {
          return await FormApi.postStep4ConnectionAndInstallation(formId, payload);
        }
      }
      throw err;
    }
  }

  static async getStep5Customer(formId: string) {
    const { data } = await Api.get<FormStep5CustomerResponse>(
      `/form-submission/${formId}/customer/`,
    );
    return data;
  }

  static async postStep5Customer(formId: string, payload: FormStep5Customer) {
    const { data } = await Api.post<FormStep5CustomerResponse>(
      `/form-submission/${formId}/customer/`,
      payload,
    );
    return data;
  }
}
